import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAutoparkById, clearCurrentAutopark } from '../../store/admin/autoparkSlice';
import { instance } from '../../api/api';
import Header from '../../components/Header/Header';
import AutoparkDetails from '../../components/AutoparkDetails/AutoparkDetails';
import AutoparkCars from '../../components/AutoparkCars/AutoparkCars';
import AutoparkDrivers from '../../components/AutoparkDrivers/AutoparkDrivers';
import AdminFinances from '../../components/AdminFinances/AdminFinances';
import AdminWaybillsList from '../../components/AdminWaybillsList/AdminWaybillsList';
import { AutoparkApiTokens } from '../../components/AutoparkApiTokens';
import './autoparkDetailsPage.css';

function AutoparkDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentAutopark: autopark, loading, error } = useAppSelector((state) => state.autopark);
  const user = useAppSelector((state) => state.user.user);

  const [activeTab, setActiveTab] = useState('autopark');

  // Partner API credentials are visible only to admin (any park) and autopark_employee (own park);
  // the backend enforces the same rule, this only hides the tab for medic/technik/driver.
  const canManagePartnerApi = user?.role === 'admin' || user?.role === 'autopark_employee';
  // Finance endpoints (/balances) are admin-only on the backend; the tab is hidden for everyone else for now.
  const canViewFinance = user?.role === 'admin';

  // Используем ref для отслеживания первичной загрузки
  const isInitialFetchRef = useRef(true);

  // Загрузка данных автопарка при монтировании компонента
  useEffect(() => {
    const loadAutopark = async () => {
      if (isInitialFetchRef.current) {
        if (id) {
          // Если есть ID в URL, загружаем по нему (для админов)
          dispatch(fetchAutoparkById(id));
        } else if (user?.role === 'autopark_employee' && user.autoparkIDs?.[0]) {
          // Для сотрудников автопарка получаем их автопарк
          try {
            dispatch(fetchAutoparkById(user.autoparkIDs?.[0]));
          } catch (error) {
            console.error('Ошибка при получении автопарка пользователя:', error);
          }
        }
        isInitialFetchRef.current = false;
      }
    };

    loadAutopark();

    // Очищаем данные при размонтировании компонента
    return () => {
      dispatch(clearCurrentAutopark());
      isInitialFetchRef.current = true; // Сбрасываем флаг при размонтировании
    };
  }, [id, dispatch, user]);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const renderTabContent = (): JSX.Element | null => {
    if (!autopark) return null;

    switch (activeTab) {
      case 'autopark':
        return <AutoparkDetails autopark={autopark} />;
      case 'cars':
        return <AutoparkCars autopark={autopark} />;
      case 'drivers':
        return <AutoparkDrivers autoparkId={autopark.id} />;
      case 'finance':
        return canViewFinance ? <AdminFinances autopark={autopark} /> : null;
      case 'waybills':
        return <AdminWaybillsList />;
      case 'apiTokens':
        return canManagePartnerApi ? <AutoparkApiTokens autoparkId={autopark.id} /> : null;
      default:
        return <div className="tab-content">Выберите вкладку</div>;
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="autopark-details-page__loading">Загрузка...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="autopark-details-page__error">{error}</div>
      </>
    );
  }

  if (!autopark) {
    return (
      <>
        <Header />
        <div className="autopark-details-page__error">Автопарк не найден</div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="autopark-details-page">
        {/* Хлебные крошки */}
        <div className="breadcrumbs">
          <div className="breadcrumbs__container">
            <div className="breadcrumbs__icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M14.9979 9.6981C14.9979 10.6252 14.9996 11.5523 14.9979 12.4793C14.9944 13.9173 13.9503 14.9823 12.5313 14.9945C11.9842 14.9989 11.437 14.9972 10.8899 14.9954C9.8198 14.9928 9.04842 14.2273 9.03803 13.1395C9.02851 12.1176 9.04236 11.0957 9.0337 10.0739C9.02504 9.06776 8.20518 8.36543 7.2364 8.52082C6.52562 8.63408 5.99059 9.26617 5.97414 10.0273C5.96462 10.4873 5.97154 10.9474 5.97154 11.4074C5.97154 11.9842 5.975 12.561 5.97068 13.1378C5.96115 14.2088 5.19583 14.9866 4.14134 14.9963C3.55869 15.0016 2.97518 15.0016 2.39253 14.9945C1.09044 14.9787 0.0151751 13.8936 0.00911489 12.5715C0.000457377 10.6884 -0.00646863 8.80439 0.00998064 6.92127C0.0212354 5.67902 0.56233 4.69752 1.53024 3.94251C2.92756 2.85215 4.31796 1.753 5.70749 0.65298C6.79747 -0.210008 8.18527 -0.220542 9.27784 0.638933C10.7219 1.77495 12.1712 2.90482 13.6049 4.05401C14.5234 4.79058 14.9892 5.76945 14.997 6.96165C15.0022 7.8738 14.9979 8.78683 14.9979 9.69898H14.9987L14.9979 9.6981Z"
                  fill="#1D61E7"
                />
              </svg>
            </div>
            <span className="breadcrumbs__text">
              <Link to="/">Главная</Link>
              {user?.role === 'admin' && (
                <>
                  {' / '}
                  <Link to="/account/admin/autoparks">Автопарки</Link>
                </>
              )}
              {' / '}
              <span className="breadcrumbs__current">Автопарк "{autopark.tradeName || autopark.legalName}"</span>
            </span>
          </div>
        </div>

        {/* Табы навигации */}
        <div className="tabs-navigation">
          <div className="tabs-navigation__container">
            <button
              className={`tabs-navigation__tab ${activeTab === 'autopark' ? 'tabs-navigation__tab--active' : ''}`}
              onClick={() => handleTabClick('autopark')}
            >
              Автопарк
            </button>
            <button
              className={`tabs-navigation__tab ${activeTab === 'cars' ? 'tabs-navigation__tab--active' : ''}`}
              onClick={() => handleTabClick('cars')}
            >
              Автомобили
            </button>
            <button
              className={`tabs-navigation__tab ${activeTab === 'drivers' ? 'tabs-navigation__tab--active' : ''}`}
              onClick={() => handleTabClick('drivers')}
            >
              Водители
            </button>
            {canViewFinance && (
              <button
                className={`tabs-navigation__tab ${activeTab === 'finance' ? 'tabs-navigation__tab--active' : ''}`}
                onClick={() => handleTabClick('finance')}
              >
                Финансы
              </button>
            )}
            <button
              className={`tabs-navigation__tab ${activeTab === 'waybills' ? 'tabs-navigation__tab--active' : ''}`}
              onClick={() => handleTabClick('waybills')}
            >
              Путевые листы
            </button>
            {canManagePartnerApi && (
              <button
                className={`tabs-navigation__tab ${activeTab === 'apiTokens' ? 'tabs-navigation__tab--active' : ''}`}
                onClick={() => handleTabClick('apiTokens')}
              >
                Партнёрский API
              </button>
            )}
          </div>
          <div className="tabs-navigation__line"></div>
        </div>

        {/* Содержимое вкладок */}
        {renderTabContent()}
      </div>
    </>
  );
}

export default AutoparkDetailsPage;
